import axios, { AxiosError, AxiosProgressEvent } from "axios";
import { CxFeatureFlagStatus } from "../domain/app-config";
import { apiOssProxyInternal } from "./api";
import { Cohort, CohortCreate, cohortSchema, CohortUpdate } from "./schemas/cohort";
import {
  CqFacilityUpdate,
  CwFacilityUpdate,
  Facility,
  FacilityInternalCreateOrUpdate,
  facilitySchema,
} from "./schemas/facility";
import { MedicalDataSource } from "./schemas/hies";
import {
  CqDirectoryEntryData,
  cqDirectoryEntryDataSchema,
  CqOrganizationUpdate,
  CwOrganizationUpdate,
  CwOrgData,
  cwOrgDataSchema,
  Organization,
  OrganizationInternalCreateOrUpdate,
  organizationSchema,
} from "./schemas/organization";
import {
  BulkPatientImport,
  BulkPatientImportCreateResponse,
  bulkPatientImportCreateResponseSchema,
  bulkPatientImportSchema,
  CoverageAssessment,
  EhrSources,
  PatientBasicInfo,
  patientBasicInfoSchema,
  PatientCoverageResponse,
  patientCoverageResponseSchema,
} from "./schemas/patient";

export async function getCxData(
  cxId: string
): Promise<{ org: Organization | undefined; facilities: Facility[] }> {
  try {
    const resp = await apiOssProxyInternal.get(`/${cxId}/cx-data`);
    if (!resp.data) throw new Error(`Cx data not returned`);
    return {
      org: organizationSchema.parse(resp.data["org"]),
      facilities: resp.data["facilities"].map(facilitySchema.parse),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return { org: undefined, facilities: [] };
    }
    throw error;
  }
}

export async function allowMapiAccess(cxId: string): Promise<void> {
  await apiOssProxyInternal.post(`/${cxId}/mapi-access`);
}

export async function revokeMapiAccess(cxId: string): Promise<void> {
  await apiOssProxyInternal.delete(`/${cxId}/mapi-access`);
}

export async function checkMapiAccess(cxId: string): Promise<{ hasMapiAccess: boolean }> {
  const resp = await apiOssProxyInternal.get(`/${cxId}/mapi-access`);
  if (!resp.data) throw new Error(`Customer not returned`);
  return resp.data;
}

export async function getCwFacility(cxId: string, id: string, oid: string): Promise<CwOrgData> {
  const resp = await apiOssProxyInternal.get(
    `/${cxId}/commonwell/ops/organization/${oid}?facilityId=${id}`
  );
  if (!resp.data) throw new Error(`CW Organization not returned`);
  return cwOrgDataSchema.parse(resp.data);
}

export async function getCqFacility(
  cxId: string,
  id: string,
  oid: string
): Promise<CqDirectoryEntryData> {
  const resp = await apiOssProxyInternal.get(
    `/${cxId}/carequality/ops/directory/organization/${oid}?facilityId=${id}`
  );
  if (!resp.data) throw new Error(`CQ Organization not returned`);
  return cqDirectoryEntryDataSchema.parse(resp.data);
}

export async function createOrUpdateFacility(
  cxId: string,
  facilityParams: FacilityInternalCreateOrUpdate
): Promise<Facility> {
  const resp = await apiOssProxyInternal.put(`/${cxId}/facility`, facilityParams);
  if (!resp.data) throw new Error(`Facility not returned`);
  return facilitySchema.parse(resp.data);
}

export async function updateCwFacility(
  cxId: string,
  id: string,
  oid: string,
  facilityParams: CwFacilityUpdate
): Promise<void> {
  await apiOssProxyInternal.put(
    `/${cxId}/commonwell/ops/facility/${oid}?facilityId=${id}`,
    facilityParams
  );
}

export async function updateCqFacility(
  cxId: string,
  id: string,
  oid: string,
  facilityParams: CqFacilityUpdate
): Promise<void> {
  await apiOssProxyInternal.put(
    `/${cxId}/carequality/ops/directory/facility/${oid}?facilityId=${id}`,
    facilityParams
  );
}

export async function getCwOrganization(cxId: string, oid: string): Promise<CwOrgData> {
  const resp = await apiOssProxyInternal.get(`/${cxId}/commonwell/ops/organization/${oid}`);
  if (!resp.data) throw new Error(`CW Organization not returned`);
  return cwOrgDataSchema.parse(resp.data);
}

export async function getCqOrganization(cxId: string, oid: string): Promise<CqDirectoryEntryData> {
  const resp = await apiOssProxyInternal.get(
    `/${cxId}/carequality/ops/directory/organization/${oid}`
  );
  if (!resp.data) throw new Error(`CQ Organization not returned`);
  return cqDirectoryEntryDataSchema.parse(resp.data);
}

export async function createOrUpdateOrganization(
  cxId: string,
  orgParams: OrganizationInternalCreateOrUpdate
): Promise<Organization> {
  const payload = {
    ...orgParams,
    shortcode: orgParams.shortcode?.trim() ? orgParams.shortcode.trim() : undefined,
  };
  const resp = await apiOssProxyInternal.put(`/${cxId}/organization`, payload);
  if (!resp.data) throw new Error(`Organization not returned`);
  return organizationSchema.parse(resp.data);
}

export async function updateCwOrganization(
  cxId: string,
  oid: string,
  orgParams: CqOrganizationUpdate
): Promise<void> {
  await apiOssProxyInternal.put(`/${cxId}/commonwell/ops/organization/${oid}`, orgParams);
}

export async function updateCqOrganization(
  cxId: string,
  oid: string,
  orgParams: CwOrganizationUpdate
): Promise<void> {
  await apiOssProxyInternal.put(
    `/${cxId}/carequality/ops/directory/organization/${oid}`,
    orgParams
  );
}

export async function getHieEnabledStatus(cxId: string): Promise<CxFeatureFlagStatus> {
  const resp = await apiOssProxyInternal.get(`/${cxId}/cx-ff-status`);
  if (!resp.data) throw new Error(`Feature Flag Status not returned`);
  return {
    cxsWithCWFeatureFlag: resp.data.cxsWithCWFeatureFlag,
    cxsWithCQDirectFeatureFlag: resp.data.cxsWithCQDirectFeatureFlag,
    cxsWithEhexEnabled: resp.data.cxsWithEhexEnabled,
    cxsWithEpicEnabled: resp.data.cxsWithEpicEnabled,
    cxsWithDemoAugEnabled: resp.data.cxsWithDemoAugEnabled,
    cxsWithAdtRosterUploadEnabled: resp.data.cxsWithAdtsRosterUploadEnabledFeatureFlag,
    cxsWithAdtDataVisibleEnabled: resp.data.cxsWithAdtsDataVisibleEnabledFeatureFlag,
    cxsWithAiBriefEnabled: resp.data.cxsWithAiBriefFeatureFlag,
  };
}

export async function updateHieEnabledStatus({
  cxId,
  flags,
}: {
  cxId: string;
  flags: {
    cwEnabled?: boolean;
    cqEnabled?: boolean;
    ehexEnabled?: boolean;
    epicEnabled?: boolean;
    demoAugEnabled?: boolean;
    adtRosterUploadEnabled?: boolean;
    adtDataVisibleEnabled?: boolean;
    aiBriefEnabled?: boolean;
  };
}): Promise<CxFeatureFlagStatus> {
  const params = new URLSearchParams();
  if (flags.cwEnabled !== undefined) params.set("cwEnabled", `${flags.cwEnabled}`);
  if (flags.cqEnabled !== undefined) params.set("cqEnabled", `${flags.cqEnabled}`);
  if (flags.ehexEnabled !== undefined) params.set("ehexEnabled", `${flags.ehexEnabled}`);
  if (flags.epicEnabled !== undefined) params.set("epicEnabled", `${flags.epicEnabled}`);
  if (flags.demoAugEnabled !== undefined) params.set("demoAugEnabled", `${flags.demoAugEnabled}`);
  if (flags.adtRosterUploadEnabled !== undefined)
    params.set("adtRosterUploadEnabledFeatureFlag", `${flags.adtRosterUploadEnabled}`);
  if (flags.adtDataVisibleEnabled !== undefined)
    params.set("adtDataVisibleEnabledFeatureFlag", `${flags.adtDataVisibleEnabled}`);
  if (flags.aiBriefEnabled !== undefined) params.set("aiBriefEnabled", `${flags.aiBriefEnabled}`);
  const resp = await apiOssProxyInternal.put(`/${cxId}/cx-ff-status?${params.toString()}`);
  if (!resp.data) throw new Error(`Customer not returned`);
  return {
    cxsWithCWFeatureFlag: resp.data.cxsWithCWFeatureFlag,
    cxsWithCQDirectFeatureFlag: resp.data.cxsWithCQDirectFeatureFlag,
    cxsWithEhexEnabled: resp.data.cxsWithEhexEnabled,
    cxsWithEpicEnabled: resp.data.cxsWithEpicEnabled,
    cxsWithDemoAugEnabled: resp.data.cxsWithDemoAugEnabled,
    cxsWithAdtRosterUploadEnabled: resp.data.cxsWithAdtsRosterUploadEnabledFeatureFlag,
    cxsWithAdtDataVisibleEnabled: resp.data.cxsWithAdtsDataVisibleEnabledFeatureFlag,
    cxsWithAiBriefEnabled: resp.data.cxsWithAiBriefFeatureFlag,
  };
}

export async function createCoverageAssessment({
  cxId,
  coverageAssessment,
  facilityId,
  validate = false,
}: {
  cxId: string;
  coverageAssessment: CoverageAssessment;
  facilityId: string;
  validate?: boolean;
}): Promise<void> {
  await apiOssProxyInternal.post(
    `/${cxId}/patient/bulk/coverage-assessment?facilityId=${facilityId}&dryrun=${validate}`,
    coverageAssessment
  );
}

export async function getCoverageAssessment({
  cxId,
  facilityId,
}: {
  cxId: string;
  facilityId: string;
}): Promise<PatientCoverageResponse> {
  const resp = await apiOssProxyInternal.get(
    `/${cxId}/patient/bulk/coverage-assessment?facilityId=${facilityId}`
  );
  if (!resp.data) throw new Error(`Coverage assessment patient data not returned`);
  return patientCoverageResponseSchema.parse(resp.data);
}

export async function createBulkPatientImport(
  cxId: string,
  facilityId: string,
  dryRun: boolean
): Promise<BulkPatientImportCreateResponse> {
  const response = await apiOssProxyInternal.post<BulkPatientImportCreateResponse>(
    `/${cxId}/patient/bulk?facilityId=${facilityId}&dryRun=${dryRun}`
  );
  if (!response.data) throw new Error("No data returned from create bulk patient import");
  return bulkPatientImportCreateResponseSchema.parse(response.data);
}

export async function uploadBulkPatientImportFile(uploadUrl: string, file: File): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      const progress = Math.round((progressEvent.loaded / (progressEvent.total ?? 0)) * 100);
      console.log(`Upload progress: ${progress}%`);
    },
  });
}

export async function getBulkPatientImports(
  cxId: string,
  facilityId: string
): Promise<BulkPatientImport[]> {
  const response = await apiOssProxyInternal.get(`/${cxId}/patient/bulk?facilityId=${facilityId}`);
  if (!response.data) throw new Error("No data returned from get bulk patient imports");
  return bulkPatientImportSchema.array().parse(response.data);
}

export type BulkPatientImportWithDebugInfo = BulkPatientImport & Record<string, unknown>;

export async function getBulkPatientImport(
  cxId: string,
  jobId: string
): Promise<BulkPatientImportWithDebugInfo> {
  const response = await apiOssProxyInternal.get(`/${cxId}/patient/bulk/${jobId}?level=info`);
  if (!response.data) throw new Error("No data returned from get bulk patient imports");
  return bulkPatientImportSchema.passthrough().parse(response.data);
}

export async function getCxCohorts(cxId: string): Promise<Cohort[]> {
  const response = await apiOssProxyInternal.get(`/${cxId}/cohort`);
  if (!response.data) throw new Error("No data returned from get customer cohorts");
  const cohorts = response.data.cohorts;
  return cohortSchema.array().parse(cohorts);
}

export async function updateCohort(
  cxId: string,
  cohortId: string,
  data: CohortUpdate
): Promise<Cohort> {
  const response = await apiOssProxyInternal.patch(`/${cxId}/cohort/${cohortId}`, data);
  if (!response.data) throw new Error("No data returned from update cohort");
  return cohortSchema.parse(response.data);
}

export async function createCohort(cxId: string, data: CohortCreate): Promise<Cohort> {
  const response = await apiOssProxyInternal.post(`/${cxId}/cohort/`, data);
  if (!response.data) throw new Error("No data returned from create cohort");
  return cohortSchema.parse(response.data);
}

function parseJsonBody(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch (error) {
    const parseError = error instanceof Error ? error.message : String(error);
    throw new Error(`Invalid JSON: ${parseError}`);
  }
}

export async function addPatientsToCohort({
  cxId,
  cohortId,
  body,
}: {
  cxId: string;
  cohortId: string;
  body: string;
}): Promise<Cohort> {
  const data = parseJsonBody(body);
  const response = await apiOssProxyInternal.post(`/${cxId}/cohort/${cohortId}/patient`, data);
  return cohortSchema.parse(response.data.cohort);
}

export async function removePatientsFromCohort({
  cxId,
  cohortId,
  body,
}: {
  cxId: string;
  cohortId: string;
  body: string;
}): Promise<Cohort> {
  const data = parseJsonBody(body);
  const response = await apiOssProxyInternal.delete(`/${cxId}/cohort/${cohortId}/patient`, {
    data,
  });
  return cohortSchema.parse(response.data.cohort);
}

export async function uploadRosterToHie({
  cxId,
  config,
}: {
  cxId: string;
  config: string;
}): Promise<void> {
  const data = parseJsonBody(config);
  await apiOssProxyInternal.post(`/${cxId}/adts/roster-upload`, data);
}

export async function getCohortOverrides(
  cxId: string,
  cohortId: string
): Promise<Record<string, boolean>> {
  const response = await apiOssProxyInternal.get(`/${cxId}/cohort/${cohortId}/overrides`);
  return response.data;
}

export async function getPatientByExternalId({
  cxId,
  externalId,
  source,
}: {
  cxId: string;
  externalId: string;
  source: EhrSources;
}): Promise<PatientBasicInfo | undefined> {
  const resp = await apiOssProxyInternal.get(
    `/${cxId}/patient/external-id?externalId=${encodeURIComponent(externalId)}&source=${source}`
  );
  if (!resp.data) return undefined;
  return patientBasicInfoSchema.parse(resp.data);
}

export async function runPatientDiscovery({
  cxId,
  patientId,
  hie,
}: {
  cxId: string;
  patientId: string;
  hie: "all" | MedicalDataSource;
}): Promise<void> {
  const urlParams = hie === "all" ? undefined : new URLSearchParams({ hie });
  const queryString = urlParams ? `?${urlParams.toString()}` : "";
  await apiOssProxyInternal.post(`/${cxId}/patient/${patientId}/patient-discovery${queryString}`);
  return;
}
